import React from 'react';
import { DrawerBody } from '@one-thd/sui-atomic-components';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { FloatingDatePill } from './subcomponents/FloatingDatePill';
import { useDrawer } from './DrawerProvider';
import { DrawerInput } from './subcomponents/DrawerInput';
import { DrawerInputWithFileUpload } from './subcomponents/DrawerInputWithFileUpload';

// DrawerContent is the drawer BODY frame: a scrollable message area (the active journey's
// content is injected as `children`) plus the sticky composer input. It owns no message
// state — the conversation list lives in the journey's ChatHistory. The file-upload flag
// picks the richer composer.
//
// The MINIMIZED peek is chips and nothing else. Neither the composer nor the floating date
// pill renders there, and that is a hard requirement rather than a style choice: the peek is
// a fixed DRAWER_HEIGHTS.MINIMIZED box under `sui-overflow-y-hidden`, so anything rendered
// beside the chips does not overflow into a scroll — it silently eats the chips' height. The
// composer previously rendered in every state (the isMinimized check only chose *which*
// composer), and together with the handlebar it consumed the whole peek, collapsing the
// flexing body to ~0 and making the chip row disappear.
//
// The composer is wrapped in `sui-shrink-0` because this element owns the flex column: only
// the body may flex. sui's DrawerFooter carries shrink-0 on its `flex` variant alone, and
// that variant drops the `sui-relative` ScrollToBottomButton is positioned against.
export const DrawerContent = ({ children, overlay = null }) => {
  const isUploadEnabled = useConfigService('magic-apron-file-upload-enabled');

  const { onScroll, scrollEl, drawerState } = useDrawer();

  // Use drawerState instead of CSS media queries for mobile
  const isMinimized = drawerState === 'MINIMIZED';

  // Inline: DrawerBody spreads props after its own className, so passing one would drop its
  // background and overflow classes. ALL padding goes while minimized — the chip row brings
  // its own. See .claude/context/drawer-layout.md
  const bodyStyle = {
    overscrollBehavior: 'contain',
    zIndex: 0,
    ...(isMinimized && { padding: 0 }),
  };

  const drawerBodyClasses = classnames(
    'sui-flex sui-flex-col sui-justify-stretch sui-flex-1 sui-min-h-0 sui-relative',
  );

  return (
    <div id="magic-apron-drawer-content" data-component="DrawerContent" className={drawerBodyClasses}>
      {/* Takeover slot (the post-chat survey): covers the composer too, and is sized by this
          element's definite height. See .claude/context/drawer-layout.md */}
      {overlay}
      <DrawerBody
        overflow="scroll"
        style={bodyStyle}
        data-testid="drawer-body"
        ref={scrollEl}
        onScroll={onScroll}
      >
        {!isMinimized && <FloatingDatePill />}
        {children}
      </DrawerBody>
      {!isMinimized && (
        <div className="sui-shrink-0">
          {isUploadEnabled ? <DrawerInputWithFileUpload /> : <DrawerInput />}
        </div>
      )}
    </div>
  );
};

DrawerContent.propTypes = {
  children: PropTypes.node.isRequired,
  overlay: PropTypes.node,
};
